import { useMemo, useState } from "react";
import type { RecurringPlan, Client } from "../../../types";
import { navigate } from "../../../lib/router";
import { Switch } from "../../../components/ui/switch";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { useLanguage } from "../../../contexts/LanguageContext";
import { formatWeekday, formatTime } from "../../utils/schedule";
import { FileText, Loader2, Copy, Check, Trash2, Package, Calendar } from "lucide-react";
import { api } from "../../../api";
import { toast } from "sonner";
import { format } from "date-fns";
import { clientDetailsLabels } from "../labels";

interface RecurringPlansSectionProps {
  plans: RecurringPlan[];
  client: Client | null;
  contracts?: any[];
  onContractGenerated?: () => void;
}

export function RecurringPlansSection({
  plans,
  client,
  contracts = [],
  onContractGenerated,
}: RecurringPlansSectionProps) {
  const { lang } = useLanguage();
  const t = clientDetailsLabels[lang].recurringPlansSection;

  const [showInactive, setShowInactive] = useState(false);
  const [generatingId, setGeneratingId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const filteredPlans = useMemo(() => {
    if (showInactive) {
      return plans;
    }
    return plans.filter((p) => p.active);
  }, [plans, showInactive]);

  const handleGenerateContract = async (plan: RecurringPlan) => {
    if (!client) return;
    setGeneratingId(plan.id);

    const compRes = await api.getCompany();
    if (!compRes.ok) {
      toast.error(t.errors.fetchTemplate);
      setGeneratingId(null);
      return;
    }

    const template = compRes.data.contract_template;
    if (!template) {
      toast.error(t.errors.noTemplate);
      setGeneratingId(null);
      return;
    }

    let content = template;
    const vars: Record<string, string> = {
      "{{nome_cliente}}": client.name || "",
      "{{cpf_cliente}}": client.cpf || "",
      "{{rg_cliente}}": (client as any).rg || "",
      "{{endereco_cliente}}": `${client.street || ""}, ${client.address_number || ""}${
        client.complement ? " - " + client.complement : ""
      }`,
      "{{nome_empresa}}": compRes.data.name || "",
      "{{cnpj_empresa}}": compRes.data.cnpj || "",
      "{{nome_plano}}": plan.name || "",
      "{{valor_plano}}": `R$ ${(plan.price_cents / 100).toFixed(2).replace(".", ",")}`,
      "{{data_hoje}}": format(new Date(), "dd/MM/yyyy"),
    };

    Object.entries(vars).forEach(([key, val]) => {
      content = content.replace(new RegExp(key, "g"), val);
    });

    const res = await api.createClientContract(client.id, {
      title: `${lang === "pt" ? "Contrato" : "Contract"} - ${plan.name}`,
      content: content,
    });

    if (res.ok) {
      toast.success(t.toasts.generated);
      if (onContractGenerated) onContractGenerated();
    } else {
      toast.error(t.errors.generateFailed);
    }
    setGeneratingId(null);
  };

  const copyToClipboard = (url: string, id: number) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success(t.linkCopied);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getPendingContractForPlan = (planName: string) => {
    return contracts.find((c) => c.status === "pending" && c.title.includes(planName));
  };

  const getSignedContractForPlan = (planName: string) => {
    return contracts.find((c) => c.status === "signed" && c.title.includes(planName));
  };

  return (
    <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-neutral-50 flex items-center justify-between bg-gradient-to-r from-white to-neutral-50/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <Package className="w-4 h-4" />
          </div>
          <span className="font-bold text-neutral-800 tracking-tight">{t.title}</span>
        </div>
        <div 
          className="h-9 px-4 rounded-xl border border-neutral-100 flex items-center gap-3 cursor-pointer hover:bg-white transition-all bg-neutral-50/50"
          onClick={() => setShowInactive(!showInactive)}
        >
          <span className="text-[10px] font-bold uppercase text-neutral-400 tracking-widest select-none">
            {t.showInactive}
          </span>
          <Switch 
            checked={showInactive} 
            onCheckedChange={setShowInactive} 
          />
        </div>
      </div>

      <div className="p-4 space-y-3">
        {filteredPlans.length === 0 ? (
          <div className="py-8 text-center text-sm font-medium text-neutral-400 italic">{t.empty}</div>
        ) : (
          filteredPlans.map((p) => {
            const pendingContract = getPendingContractForPlan(p.name);
            const signedContract = getSignedContractForPlan(p.name);

            return (
              <div
                key={p.id}
                className="w-full border border-neutral-50 rounded-2xl p-4 hover:bg-neutral-50/50 transition-all group"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="cursor-pointer flex-1" onClick={() => navigate(`/app/plans/${p.id}`)}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-bold text-neutral-900 group-hover:text-primary transition-colors">
                        {p.name || `Plano #${p.id}`}
                      </span>
                      {!p.active && (
                        <Badge variant="danger" className="text-[9px] uppercase font-bold px-2 py-0.5">
                          {t.inactiveBadge}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(p.schedules || []).map((s) => (
                        <span
                          key={s.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-neutral-100 text-[10px] font-bold text-neutral-500 shadow-sm"
                        >
                          <Calendar className="w-3 h-3 opacity-40" />
                          {formatWeekday(s.weekday, lang)} {formatTime(s.start_time, lang)}–{formatTime(s.end_time, lang)}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between gap-3">
                    <div className="text-lg font-bold text-emerald-600 tabular-nums">
                      R$ ${(p.price_cents / 100).toFixed(2).replace(".", ",")}
                    </div>

                    <div className="flex gap-2">
                      {signedContract && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="micro"
                            variant="secondary"
                            className="h-8 px-3 text-[10px] font-bold uppercase tracking-widest gap-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-100 rounded-xl"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (signedContract.file_url) window.open(signedContract.file_url, "_blank");
                            }}
                          >
                            <FileText className="h-3.5 w-3.5" />
                            {t.downloadContract}
                          </Button>
                          <Button
                            size="micro"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl"
                            title={t.cancelContract}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (confirm(t.confirms.deleteSigned)) {
                                await api.deleteClientContract(client!.id, signedContract.id);
                                if (onContractGenerated) onContractGenerated();
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}

                      {pendingContract?.signing_url && !signedContract && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="micro"
                            variant="secondary"
                            className="h-8 px-3 text-[10px] font-bold uppercase tracking-widest gap-2 bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-100 rounded-xl"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(pendingContract.signing_url!, p.id);
                            }}
                          >
                            {copiedId === p.id ? (
                              <Check className="h-3.5 w-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                            {t.copyLink}
                          </Button>
                          <Button
                            size="micro"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl"
                            title={t.deleteContract}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (confirm(t.confirms.deletePending)) {
                                await api.deleteClientContract(client!.id, pendingContract.id);
                                if (onContractGenerated) onContractGenerated();
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}

                      {!pendingContract && !signedContract && (
                        <Button
                          size="micro"
                          variant="outline"
                          className="h-8 px-3 text-[10px] font-bold uppercase tracking-widest gap-2 border-neutral-200 hover:bg-neutral-50 rounded-xl transition-all active:scale-95"
                          disabled={generatingId === p.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateContract(p);
                          }}
                        >
                          {generatingId === p.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <FileText className="h-3.5 w-3.5" />
                          )}
                          {t.generateContract}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
